import moment from 'moment-timezone'
import { formatCurrency } from '@smarttransit/common'
import { isAuthorized, addAlert } from '../../utilities/helpers'
import { findTransportationProfiles } from '../../services/transportation-profiles-service'
import {
  createAdHocRevenueTransaction,
  updateAdHocRevenueTransaction,
  findAgentTopupsForAdHocRevenue
} from '../../services/passenger-transaction-logs-service'

function normalizeRevenueDate (dateValue) {
  if (!dateValue) {
    return null
  }

  const dateAsMoment = moment(dateValue)

  if (!dateAsMoment.isValid()) {
    return null
  }

  return dateAsMoment.format('Y-MM-DD')
}

export default {
  name: 'ad-hoc-revenue-dialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    signedInUser: {
      type: Object,
      default: null
    },
    initialRevenue: {
      type: Object,
      default: null
    },
    lockedVehicle: {
      type: Object,
      default: null
    },
    transportationOwnerId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      vehiclesApiInProgress: false,
      vehiclesLoaded: [],
      vehicles: [],
      adHocVehicleKeywords: '',
      addingAdhocRevenue: false,
      adhocRevenue: null,
      deductAgentTopups: false,
      agentTopups: [],
      agentTopupsApiInProgress: false,
      selectedAgentProfileIds: [],
      selectedCurrencySymbol: ''
    }
  },
  computed: {
    dialogModel: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    isVehicleLocked () {
      return !!(this.lockedVehicle && this.lockedVehicle.id)
    },
    selectedVehicle () {
      if (this.isVehicleLocked) {
        return this.lockedVehicle
      }

      if (!this.adhocRevenue || !this.adhocRevenue.transportationProfileId) {
        return null
      }

      return (this.vehiclesLoaded || []).find((vehicle) => vehicle.id === this.adhocRevenue.transportationProfileId)
    },
    selectedAgentTopups () {
      return (this.agentTopups || []).filter((agentTopup) => this.selectedAgentProfileIds.includes(agentTopup.agentProfileId))
    },
    agentTopupDeductionTotal () {
      return this.selectedAgentTopups.reduce((sum, agentTopup) => {
        return sum + Number(agentTopup.netPassengerCredits || 0)
      }, 0)
    },
    finalAmountToSubmit () {
      if (!this.deductAgentTopups) {
        return Number(this.adhocRevenue && this.adhocRevenue.amount ? this.adhocRevenue.amount : 0)
      }

      return Number(this.adhocRevenue && this.adhocRevenue.amount ? this.adhocRevenue.amount : 0) - this.agentTopupDeductionTotal
    },
    allAgentTopupsSelected () {
      return this.agentTopups.length > 0 && this.selectedAgentProfileIds.length === this.agentTopups.length
    },
    anyAgentTopupsSelected () {
      return this.selectedAgentProfileIds.length > 0
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (val) {
          this.initAdHocRevenue()
          this.loadVehiclesIfNeeded()
          this.resolveCurrencySymbol()
        }
      }
    },
    initialRevenue: {
      deep: true,
      handler () {
        if (this.value) {
          this.initAdHocRevenue()
        }
      }
    },
    lockedVehicle: {
      deep: true,
      handler () {
        if (this.value) {
          this.initAdHocRevenue()
          this.loadVehiclesIfNeeded()
          this.resolveCurrencySymbol()
        }
      }
    },
    transportationOwnerId () {
      this.loadVehiclesIfNeeded()
    },
    selectedVehicle () {
      this.resolveCurrencySymbol()
    },
    deductAgentTopups (val) {
      if (val) {
        this.loadAgentTopups()
      } else {
        this.agentTopups = []
        this.selectedAgentProfileIds = []
      }
    },
    'adhocRevenue.dateCreated' () {
      if (this.deductAgentTopups) {
        this.loadAgentTopups()
      }
    },
    'adhocRevenue.transportationProfileId' () {
      if (this.deductAgentTopups) {
        this.loadAgentTopups()
      }
    },
    adHocVehicleKeywords (val) {
      clearTimeout(this.adHocVehicleKeywordsTimeout)

      this.adHocVehicleKeywordsTimeout = setTimeout(() => {
        if (val) {
          this.vehicles = (this.vehiclesLoaded || []).filter((o) => (new RegExp(`.*${val}.*`, 'ui')).test(o.licensePlateNumber))

          if (!this.vehicles.length) {
            this.vehicles = this.vehiclesLoaded.slice()
          }
        } else {
          this.vehicles = this.vehiclesLoaded.slice()
        }
      }, 500)
    }
  },
  methods: {
    initAdHocRevenue () {
      const selectedRevenue = this.initialRevenue || {}

      this.adhocRevenue = {
        ...selectedRevenue,
        amount: selectedRevenue.amount || null,
        dateCreated: normalizeRevenueDate(selectedRevenue.dateCreated),
        notes: selectedRevenue.notes || (selectedRevenue.logMetadata && selectedRevenue.logMetadata.notes ? selectedRevenue.logMetadata.notes : ''),
        confirmationId: selectedRevenue.confirmationId || (selectedRevenue.logMetadata && selectedRevenue.logMetadata.confirmationId ? selectedRevenue.logMetadata.confirmationId : ''),
        transportationProfileId: selectedRevenue.transportationProfileId || null
      }

      if (this.isVehicleLocked) {
        this.adhocRevenue.transportationProfileId = this.lockedVehicle.id
      }

      this.deductAgentTopups = false
      this.agentTopups = []
      this.selectedAgentProfileIds = []
    },
    resolveCurrencySymbol () {
      const currencies = this.$store.getters.getCountryCurrencies || []
      const selectedVehicle = this.selectedVehicle || {}
      const selectedCurrency = currencies.find((currency) => currency.id === selectedVehicle.countryCurrencyId)
      this.selectedCurrencySymbol = selectedCurrency ? selectedCurrency.currencySymbol : ''
    },
    async loadVehiclesIfNeeded () {
      if (!this.value || this.isVehicleLocked) {
        return
      }

      try {
        const transportationOwnerId = this.transportationOwnerId || null
        this.vehiclesApiInProgress = true

        this.vehiclesLoaded = await findTransportationProfiles({
          join: transportationOwnerId ? {
            relation: 'transportationOwnerProfile',
            scope: { where: { transportationOwnerId } }
          } : undefined
        })

        this.vehicles = this.vehiclesLoaded.slice()
        this.resolveCurrencySymbol()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.vehiclesApiInProgress = false
      }
    },
    async loadAgentTopups () {
      if (!this.value || !this.deductAgentTopups) {
        return
      }

      if (!this.adhocRevenue.dateCreated || !this.adhocRevenue.transportationProfileId) {
        this.agentTopups = []
        this.selectedAgentProfileIds = []
        return
      }

      try {
        this.agentTopupsApiInProgress = true
        const agentTopups = await findAgentTopupsForAdHocRevenue({
          transportationProfileId: this.adhocRevenue.transportationProfileId,
          dateCreated: this.adhocRevenue.dateCreated
        })

        this.agentTopups = Array.isArray(agentTopups) ? agentTopups : []
        this.selectedAgentProfileIds = []
      } catch (err) {
        this.agentTopups = []
        this.selectedAgentProfileIds = []
        addAlert({ message: err, type: 'error' })
      } finally {
        this.agentTopupsApiInProgress = false
      }
    },
    toggleAgentTopupSelection (agentProfileId) {
      if (this.selectedAgentProfileIds.includes(agentProfileId)) {
        this.selectedAgentProfileIds = this.selectedAgentProfileIds.filter((id) => id !== agentProfileId)
      } else {
        this.selectedAgentProfileIds = [...this.selectedAgentProfileIds, agentProfileId]
      }
    },
    toggleAllAgentTopups () {
      if (this.allAgentTopupsSelected) {
        this.selectedAgentProfileIds = []
      } else {
        this.selectedAgentProfileIds = this.agentTopups.map((agentTopup) => agentTopup.agentProfileId)
      }
    },
    formatAmount (amount, decimalPlace) {
      return formatCurrency(amount, this.selectedCurrencySymbol || null, decimalPlace)
    },
    formatAmountForConfirmation (amount) {
      return formatCurrency(amount, null, '0.00')
    },
    getSelectedRowsForNote () {
      return this.selectedAgentTopups.map((agentTopup) => {
        const fullName = agentTopup.fullName || 'Unknown Agent'
        const agentPhone = agentTopup.agentPhone || 'No phone'
        const credits = this.formatAmount(agentTopup.netPassengerCredits)
        return fullName + ' (' + agentPhone + ') - ' + credits
      }).join('; ')
    },
    closeDialog () {
      this.dialogModel = false
    },
    async saveAdhocRevenue () {
      const finalAmountToSubmit = this.finalAmountToSubmit

      if (!finalAmountToSubmit || finalAmountToSubmit <= 0) {
        if (isAuthorized(this.signedInUser, 'superadmin') && finalAmountToSubmit === 0) {
          return alert('Please enter a valid amount other than 0')
        } else if (!isAuthorized(this.signedInUser, 'superadmin') && finalAmountToSubmit < 0) {
          return alert('Please enter a valid amount greater than 0')
        }
      }

      if (!this.adhocRevenue.dateCreated) {
        return alert('Please select a date')
      }

      if (!this.adhocRevenue.transportationProfileId) {
        return alert('Please select a vehicle')
      }

      if (this.deductAgentTopups && !this.selectedAgentProfileIds.length) {
        return alert('Select at least one agent top-up to deduct, or turn off the deduction mode')
      }

      const confirmAmount = prompt(`Please enter the amount number ${this.formatAmountForConfirmation(finalAmountToSubmit)} to confirm the manual entry`)

      if (confirmAmount === this.formatAmountForConfirmation(finalAmountToSubmit)) {
        this.addingAdhocRevenue = true

        try {
          // const agentNote = this.deductAgentTopups && this.selectedAgentTopups.length
          //   ? 'Deducted agent top-ups (' + this.selectedAgentTopups.length + '): ' + this.getSelectedRowsForNote()
          //   : ''
          const finalNotes = this.adhocRevenue.notes

          if (this.adhocRevenue.id) {
            await updateAdHocRevenueTransaction({
              id: this.adhocRevenue.id,
              transportationProfileId: this.adhocRevenue.transportationProfileId,
              amount: finalAmountToSubmit,
              dateCreated: moment(this.adhocRevenue.dateCreated, 'Y-MM-DD').utc().toDate().toISOString(),
              notes: finalNotes,
              confirmationId: this.adhocRevenue.confirmationId
            })
          } else {
            await createAdHocRevenueTransaction({
              transportationProfileId: this.adhocRevenue.transportationProfileId,
              amount: finalAmountToSubmit,
              dateCreated: moment(this.adhocRevenue.dateCreated, 'Y-MM-DD').utc().toDate().toISOString(),
              notes: finalNotes,
              confirmationId: this.adhocRevenue.confirmationId
            })
          }

          this.closeDialog()
          this.$emit('saved')
        } catch (err) {
          addAlert({ message: err, type: 'error', isModal: true })
        } finally {
          this.addingAdhocRevenue = false
        }
      } else {
        alert('Amount entered does not match the amount to confirm')
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.adHocVehicleKeywordsTimeout)
  }
}
